import { useEffect, useRef, useState } from 'react';
import { Box, Column, Typography } from '@tempmee/react';
import { type Control, useFormContext, useWatch, type UseFormWatch } from 'react-hook-form';
import { TipBanner } from '~/components/shared/TipBanner';
import { useAutoConfirmExperiment } from '~/hooks/useAutoConfirmExperiment';
import { PaymentLockedBanner } from '../common';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { AutoConfirmField, isAutoConfirmAvailable } from './AutoConfirmField';
import { InviteField } from './InviteField';
import { InviteSubCard } from './InviteSubCard';

interface ISpeedUpCoverageSectionProps {
  control: Control<IShiftDetailsFormValues>;
  watch: UseFormWatch<IShiftDetailsFormValues>;
}

const INVITE_TOOLTIP = (
  <Typography as="span" bold color="#FFFFFF">
    Please note, invites will not be delivered to professionals on requested dates in which they are
    unavailable.
  </Typography>
);

export const SpeedUpCoverageSection = ({ control, watch }: ISpeedUpCoverageSectionProps) => {
  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');
  const officeId = watch('officeId');
  const inviteIds = useWatch({ control, name: 'invite' }) ?? [];

  const { setValue, formState } = useFormContext<IShiftDetailsFormValues>();
  const {
    isEligible,
    eligibilityLoading,
    favoritesLoading,
    favoritesDisabled,
    shouldDefaultOn,
    defaultAutoConfirm,
    exposeExperiment,
  } = useAutoConfirmExperiment(officeId);

  // Default to the unlocked (valid) state while payment eligibility is still
  // resolving so the payment-locked banner never flashes before the query
  // settles; only lock once we know the office cannot pay.
  const locked = !isEligible && !eligibilityLoading;
  const autoConfirmAvailable = isAutoConfirmAvailable(professionType, preferredSpecialty);
  const isAutoConfirmDirty = Boolean(formState.dirtyFields.autoConfirm);

  // Tracks whether the invite list has no professionals. When locked, the invite
  // card stays active in the empty state (nothing to interact with) but is dimmed
  // when there is a populated list to lock.
  const [inviteEmpty, setInviteEmpty] = useState(false);

  // When payment becomes invalid, the invite list and auto-confirm are gated.
  // Clear any selection / auto-confirm captured beforehand so no stale, locked
  // values are submitted. Only runs while locked, never overriding user input
  // once payment is valid again.
  useEffect(() => {
    if (!locked) return;
    setValue('invite', [], { shouldDirty: false });
    setValue('inviteNames', {}, { shouldDirty: false });
    setValue(
      'autoConfirm',
      { favorites: false, eligibleProfessionals: false },
      { shouldDirty: false },
    );
  }, [locked, setValue]);

  // Experiment exposure: only eligible offices that actually reach an available
  // Auto-Confirm section are exposed, so the control group is not diluted by offices that were
  // never candidates for the default-on treatment. `exposeExperiment` is idempotent per office.
  useEffect(() => {
    if (locked || !autoConfirmAvailable) return;
    exposeExperiment();
  }, [locked, autoConfirmAvailable, exposeExperiment]);

  // Default Auto-Confirm ON for the eligible test group. Applied once, only while the field is
  // still untouched, and only after favorites have resolved (so the favorites default reflects
  // whether the office actually has any). Uses shouldDirty:false so it never masks a real choice.
  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultRef.current) return;
    if (locked || !autoConfirmAvailable || !shouldDefaultOn) return;
    if (favoritesLoading || isAutoConfirmDirty) return;

    appliedDefaultRef.current = true;
    setValue('autoConfirm', defaultAutoConfirm, { shouldDirty: false });
  }, [
    locked,
    autoConfirmAvailable,
    shouldDefaultOn,
    favoritesLoading,
    isAutoConfirmDirty,
    defaultAutoConfirm,
    setValue,
  ]);

  return (
    <Column data-testid="speed-up-coverage-section" data-cy="speed-up-coverage-section" gap={3}>
      {locked ? (
        <Box style={{ marginTop: -8 }}>
          <PaymentLockedBanner context="invite" />
        </Box>
      ) : (
        inviteIds.length === 0 && (
          <Box style={{ marginTop: -8 }}>
            <TipBanner
              visible
              description="Secure coverage with ease! Shifts with invites fill 2x faster."
            />
          </Box>
        )
      )}

      <InviteSubCard
        title="Invite Professionals:"
        tooltip={INVITE_TOOLTIP}
        testId="invite-sub-card"
        disabled={locked && !inviteEmpty}
      >
        <InviteField
          control={control}
          professionType={professionType}
          officeId={officeId}
          disabled={locked}
          onListEmptyChange={setInviteEmpty}
        />
      </InviteSubCard>

      <InviteSubCard title="Auto-Confirm:" testId="auto-confirm-sub-card" disabled={locked}>
        <AutoConfirmField
          control={control}
          professionType={professionType}
          preferredSpecialty={preferredSpecialty}
          eventContext="shift_request"
          favoritesDisabled={favoritesDisabled}
          hideHeader
          disabled={locked}
        />
      </InviteSubCard>
    </Column>
  );
};
